import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Plus, Check, X, RefreshCw, Wrench,
  Package, Euro, Camera, ChevronDown, ChevronUp, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateUtils';

import { getToken } from '../lib/authToken';
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const INCIDENT_TYPES = {
  casse:     { label: 'Casse',     color: 'var(--admin-red)', emoji: '💥' },
  manquant:  { label: 'Manquant',  color: '#F97316', emoji: '❓' },
  salissure: { label: 'Salissure', color: 'var(--admin-amber)', emoji: '💧' },
  panne:     { label: 'Panne',     color: 'var(--admin-red)', emoji: '⚡' },
  autre:     { label: 'Autre',     color: '#9CA3AF', emoji: 'ℹ️' },
};

function StatCard({ label, value, color, sub }) {
  return (
    <div className="card-premium p-4">
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Clients</p>
        <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
          letterSpacing: '-.6px', color: 'var(--admin-text)', marginTop: 4 }}>SAV & Incidents</h2>
        <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>Signalements et resolution</p>
      </div>

      <p className="text-xs mb-1" style={{ color: 'var(--admin-muted)' }}>{label}</p>
      <p className="font-display text-2xl font-bold" style={{ color }}>{typeof value === 'number' ? value.toFixed(0) : value}</p>
      {sub && <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{sub}</p>}
    </div>
  );
}

export default function AdminSAV() {
  const [activeTab, setActiveTab] = useState('incidents');
  const [incidents, setIncidents] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [maintenances, setMaintenances] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    product_id: '', product_nom: '', description: '',
    type_incident: 'casse', cout_reparation: 0,
    caution_utilisee: false, montant_caution: 0,
  });

  const token = getToken();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inc, alts, maint, prods] = await Promise.all([
        fetch(`${BACKEND}/api/admin/sav/incidents?limit=50`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/admin/sav/alertes`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/admin/sav/maintenance`, { headers }).then(r => r.json()),
        fetch(`${BACKEND}/api/products?type=location&limit=100`).then(r => r.json()),
      ]);
      setIncidents(inc.incidents || []);
      setStats({ total: inc.total, cout: inc.total_cout_reparation, caution: inc.total_caution_recue, non_resolus: inc.non_resolus });
      setAlertes(alts.alertes || []);
      setMaintenances(maint.maintenances || []);
      setProducts(prods.products || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitIncident = async (e) => {
    e.preventDefault();
    if (!form.product_id || !form.description) { toast.error('Remplir les champs obligatoires'); return; }
    try {
      const res = await fetch(`${BACKEND}/api/admin/sav/incidents`, {
        method: 'POST', headers,
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success('Incident créé');
      setShowForm(false);
      setForm({ product_id: '', product_nom: '', description: '', type_incident: 'casse', cout_reparation: 0, caution_utilisee: false, montant_caution: 0 });
      load();
    } catch { toast.error('Erreur'); }
  };

  const resoudreIncident = async (id) => {
    await fetch(`${BACKEND}/api/admin/sav/incidents/${id}/resoudre`, { method: 'PATCH', headers, body: JSON.stringify({}) });
    toast.success('Incident résolu');
    load();
  };

  const traiterAlerte = async (id) => {
    await fetch(`${BACKEND}/api/admin/sav/alertes/${id}/traiter`, { method: 'PATCH', headers });
    setAlertes(prev => prev.filter(a => a.id !== id));
    toast.success('Alerte traitée');
  };

  const TABS = [
    { id: 'incidents', label: '💥 Incidents', badge: stats.non_resolus || 0 },
    { id: 'alertes', label: '⚠️ Alertes', badge: alertes.length },
    { id: 'maintenance', label: '🔧 Maintenances', badge: maintenances.length },
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--admin-red-dim)' }}>
              <Wrench size={20} style={{ color: 'var(--admin-red)' }} />
            </div>
            <h1 className="text-2xl font-display font-bold" style={{ color: 'var(--admin-text)' }}>SAV & Incidents</h1>
          </div>
          <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Suivi des incidents, alertes maintenance et réparations</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load()} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium"
            style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', color: 'var(--admin-muted)', cursor: 'pointer' }}>
            <RefreshCw size={14} />
          </button>
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold"
            style={{ background: '#EF4444', color: 'var(--admin-text)', border: 'none', cursor: 'pointer' }}>
            <Plus size={14} /> Signaler incident
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total incidents" value={stats.total || 0} color="#EF4444" />
        <StatCard label="Non résolus" value={stats.non_resolus || 0} color="#F97316" />
        <StatCard label="Coût réparations" value={`${(stats.cout || 0).toFixed(0)}€`} color="#D4AF37" />
        <StatCard label="Cautions utilisées" value={`${(stats.caution || 0).toFixed(0)}€`} color="#5DBCBF" />
      </div>

      {/* Formulaire incident */}
      {showForm && (
        <div className="card-premium p-5 mb-5" style={{ border: '2px solid rgba(239,68,68,0.3)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>Nouvel incident</h3>
            <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-muted)' }}>
              <X size={18} />
            </button>
          </div>
          <form onSubmit={submitIncident} className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--admin-text)' }}>Produit *</label>
              <select value={form.product_id}
                onChange={e => {
                  const prod = products.find(p => p.id === e.target.value);
                  setForm(prev => ({ ...prev, product_id: e.target.value, product_nom: prod?.nom || '' }));
                }}
                required className="w-full px-3 py-2 rounded-xl text-sm"
                style={{ border: '1px solid var(--admin-border)', background: '#FFF', color: 'var(--admin-text)', outline: 'none' }}>
                <option value="">-- Sélectionner un produit --</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--admin-text)' }}>Type d'incident</label>
              <select value={form.type_incident}
                onChange={e => setForm(prev => ({ ...prev, type_incident: e.target.value }))}
                className="w-full px-3 py-2 rounded-xl text-sm"
                style={{ border: '1px solid var(--admin-border)', background: '#FFF', color: 'var(--admin-text)', outline: 'none' }}>
                {Object.entries(INCIDENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.emoji} {v.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--admin-text)' }}>Description *</label>
              <textarea rows={2} value={form.description}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                required placeholder="Décrire l'incident..."
                className="w-full px-3 py-2 rounded-xl text-sm resize-none"
                style={{ border: '1px solid var(--admin-border)', background: '#FFF', color: 'var(--admin-text)', outline: 'none' }} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--admin-text)' }}>Coût réparation (€)</label>
              <input type="number" value={form.cout_reparation}
                onChange={e => setForm(prev => ({ ...prev, cout_reparation: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 rounded-xl text-sm"
                style={{ border: '1px solid var(--admin-border)', background: '#FFF', color: 'var(--admin-text)', outline: 'none' }} />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs font-semibold" style={{ color: 'var(--admin-text)', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.caution_utilisee}
                  onChange={e => setForm(prev => ({ ...prev, caution_utilisee: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: 'pointer' }} />
                Caution utilisée
              </label>
              {form.caution_utilisee && (
                <input type="number" value={form.montant_caution} placeholder="Montant"
                  onChange={e => setForm(prev => ({ ...prev, montant_caution: parseFloat(e.target.value) || 0 }))}
                  className="w-24 px-2 py-1.5 rounded-lg text-xs"
                  style={{ border: '1px solid var(--admin-border)', background: '#FFF', color: 'var(--admin-text)', outline: 'none' }} />
              )}
            </div>
            <div className="sm:col-span-2 flex gap-2 justify-end">
              <button type="button" onClick={() => setShowForm(false)}
                className="px-4 py-2 rounded-xl text-sm" style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', color: 'var(--admin-muted)', cursor: 'pointer' }}>Annuler</button>
              <button type="submit"
                className="px-5 py-2 rounded-xl text-sm font-bold" style={{ background: '#EF4444', color: 'var(--admin-text)', border: 'none', cursor: 'pointer' }}>Créer l'incident</button>
            </div>
          </form>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b mb-4" style={{ borderColor: 'var(--admin-border)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2"
            style={{ borderColor: activeTab === tab.id ? '#EF4444' : 'transparent', color: activeTab === tab.id ? '#EF4444' : 'var(--text-muted)', background: 'none', cursor: 'pointer' }}>
            {tab.label}
            {tab.badge > 0 && <span className="px-1.5 py-0.5 rounded-full text-xs font-bold" style={{ background: 'var(--admin-red-dim)', color: 'var(--admin-red)' }}>{tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* INCIDENTS */}
      {activeTab === 'incidents' && (
        <div className="space-y-2">
          {loading ? [1,2].map(i => <div key={i} className="h-16 rounded-2xl animate-pulse" style={{ background: 'var(--border)' }} />) :
            incidents.length === 0 ? (
              <div className="text-center py-10">
                <Check size={32} style={{ color: '#22C55E', margin: '0 auto 8px' }} />
                <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Aucun incident enregistré</p>
              </div>
            ) : incidents.map(inc => {
              const tc = INCIDENT_TYPES[inc.type_incident] || INCIDENT_TYPES.autre;
              const isOpen = expandedId === inc.id;
              return (
                <div key={inc.id} className="rounded-2xl overflow-hidden"
                  style={{ border: `1px solid ${inc.resolu ? 'var(--border)' : `${tc.color}40`}`, background: '#FFF' }}>
                  <button onClick={() => setExpandedId(isOpen ? null : inc.id)}
                    className="w-full flex items-center gap-3 p-3 text-left"
                    style={{ cursor: 'pointer', background: 'none', border: 'none' }}>
                    <span className="text-xl flex-shrink-0">{tc.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{inc.product_nom || inc.product_id}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: `${tc.color}15`, color: tc.color }}>{tc.label}</span>
                        {inc.resolu && <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E' }}>✓ Résolu</span>}
                      </div>
                      <p className="text-xs truncate" style={{ color: 'var(--admin-muted)' }}>{inc.description}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {inc.cout_reparation > 0 && <p className="text-xs font-bold" style={{ color: 'var(--admin-red)' }}>{inc.cout_reparation}€</p>}
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{formatDate(inc.created_at)}</p>
                    </div>
                    {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-3 border-t" style={{ borderColor: 'var(--admin-border)' }}>
                      <div className="pt-3 text-xs space-y-1" style={{ color: 'var(--admin-muted)' }}>
                        {inc.client_nom && <p>👤 Client : {inc.client_nom}</p>}
                        {inc.caution_utilisee && <p>💰 Caution : {inc.montant_caution}€ utilisée</p>}
                        <p>👤 Créé par : {inc.cree_par}</p>
                      </div>
                      {!inc.resolu && (
                        <button onClick={() => resoudreIncident(inc.id)}
                          className="mt-3 flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold"
                          style={{ background: '#22C55E', color: 'var(--admin-text)', border: 'none', cursor: 'pointer' }}>
                          <Check size={12} /> Marquer résolu
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          }
        </div>
      )}

      {/* ALERTES */}
      {activeTab === 'alertes' && (
        <div className="space-y-2">
          {alertes.length === 0 ? (
            <div className="text-center py-10">
              <Check size={32} style={{ color: '#22C55E', margin: '0 auto 8px' }} />
              <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Aucune alerte active</p>
            </div>
          ) : alertes.map(al => (
            <div key={al.id} className="card-premium p-4 flex items-start gap-3"
              style={{ borderLeft: '3px solid #F97316' }}>
              <AlertTriangle size={16} style={{ color: '#F97316', flexShrink: 0, marginTop: 2 }} />
              <div className="flex-1">
                <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{al.product_nom || al.product_id}</p>
                <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{al.raison}</p>
                <p className="text-xs mt-0.5" style={{ color: '#F97316' }}>{al.type?.replace('_', ' ')}</p>
              </div>
              <button onClick={() => traiterAlerte(al.id)}
                className="px-3 py-1.5 rounded-xl text-xs font-bold flex-shrink-0"
                style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.2)', cursor: 'pointer' }}>
                Traité
              </button>
            </div>
          ))}
        </div>
      )}

      {/* MAINTENANCE */}
      {activeTab === 'maintenance' && (
        <div>
          {maintenances.length === 0 ? (
            <div className="text-center py-10">
              <Wrench size={32} style={{ color: 'var(--border)', margin: '0 auto 8px' }} />
              <p className="text-sm" style={{ color: 'var(--admin-muted)' }}>Aucune maintenance planifiée</p>
            </div>
          ) : (
            <div className="space-y-2">
              {maintenances.map(m => (
                <div key={m.id} className="card-premium p-4">
                  <div className="flex items-center gap-3">
                    <Wrench size={16} style={{ color: '#F97316' }} />
                    <div className="flex-1">
                      <p className="font-bold text-sm" style={{ color: 'var(--admin-text)' }}>{m.product_id}</p>
                      <p className="text-xs" style={{ color: 'var(--admin-muted)' }}>{m.description} · {m.type_maintenance}</p>
                      {m.cout > 0 && <p className="text-xs font-bold" style={{ color: 'var(--admin-gold)' }}>{m.cout}€</p>}
                    </div>
                    <span className="text-xs px-2 py-1 rounded-xl" style={{ background: 'rgba(249,115,22,0.1)', color: '#F97316' }}>{m.statut}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
